import { useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import type { MetaAd } from '@storefronts/common-types'
import { Card } from '@/components/ui/card'
import { Muted, Small, XSmall } from '@/components/ui/typography'
import { AdSelectionPaginationFooter } from './AdSelectionPaginationFooter'
import { cn } from '@/lib/utils'
import { AdCreativeMedia } from './AdCreativeMedia'
import {
    findMetaAdMatchingNameParam,
    metaAdHasCreative,
    sortMetaAds,
} from './metaAdSort'
import { useAdNameParameter } from './useAdNameParameter'

const ITEMS_PER_PAGE = 24

const DEFAULT_SORT = { key: 'spend' as const, direction: 'descending' as const }

interface AdGridViewProps {
    data: MetaAd[]
    selectedId?: MetaAd['id']
    onSelect: (ad: MetaAd) => void
}

export function AdGridView({ data, selectedId, onSelect }: AdGridViewProps) {
    const adName = useAdNameParameter()
    const [pageState, setPageState] = useState({ scopeKey: '', page: 0 })

    const visibleData = useMemo(() => data.filter(metaAdHasCreative), [data])

    const sortedData = useMemo(() => {
        const pinnedAd = findMetaAdMatchingNameParam(visibleData, adName)
        const dataToSort =
            adName && pinnedAd
                ? visibleData.filter((ad) => ad.id !== pinnedAd.id)
                : visibleData

        return sortMetaAds(dataToSort, DEFAULT_SORT, pinnedAd)
    }, [visibleData, adName])

    const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE)
    const pageScopeKey = sortedData.map((ad) => ad.id).join('|')
    const storedPage = pageState.scopeKey === pageScopeKey ? pageState.page : 0
    const currentPage = Math.min(storedPage, Math.max(0, totalPages - 1))
    const startIndex = currentPage * ITEMS_PER_PAGE
    const paginatedData = sortedData.slice(
        startIndex,
        startIndex + ITEMS_PER_PAGE,
    )

    const setCurrentPage = (updater: number | ((page: number) => number)) => {
        setPageState((previous) => {
            const basePage =
                previous.scopeKey === pageScopeKey ? previous.page : 0
            const nextPage =
                typeof updater === 'function' ? updater(basePage) : updater

            return { scopeKey: pageScopeKey, page: nextPage }
        })
    }

    return (
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border shadow-sm border-border">
            <div className="min-h-0 flex-1 overflow-auto p-4">
                {paginatedData.length === 0 ? (
                    <div className="flex h-24 items-center justify-center">
                        <Muted>No ads found</Muted>
                    </div>
                ) : (
                    <div className="grid grid-cols-6 gap-3">
                        {paginatedData.map((ad) => {
                            const isSelected = selectedId === ad.id

                            return (
                                <div
                                    key={ad.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onSelect(ad)}
                                    onKeyDown={(event) => {
                                        if (
                                            event.key === 'Enter' ||
                                            event.key === ' '
                                        ) {
                                            event.preventDefault()
                                            onSelect(ad)
                                        }
                                    }}
                                    className={cn(
                                        'group flex max-h-72 cursor-pointer flex-col overflow-hidden rounded-xl border text-left transition-shadow hover:shadow-md',
                                        isSelected
                                            ? 'border-info ring-2 ring-info/30'
                                            : 'border-border hover:border-input',
                                    )}
                                >
                                    <div className="relative h-36 w-full shrink-0">
                                        <AdCreativeMedia ad={ad} />
                                        {isSelected && (
                                            <div className="absolute right-2 top-2 rounded-full p-1 bg-info">
                                                <Check className="size-4 text-white" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex min-h-0 flex-1 flex-col gap-1 p-2.5">
                                        <Small
                                            className="line-clamp-2 font-medium leading-snug text-foreground"
                                            title={ad.name}
                                        >
                                            {ad.name}
                                        </Small>
                                        <XSmall className="text-muted-foreground">
                                            {ad.spend} spend
                                        </XSmall>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {totalPages > 1 && (
                <AdSelectionPaginationFooter
                    startIndex={startIndex}
                    endIndex={Math.min(
                        startIndex + ITEMS_PER_PAGE,
                        sortedData.length,
                    )}
                    totalCount={sortedData.length}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPrevious={() =>
                        setCurrentPage((prev) => Math.max(0, prev - 1))
                    }
                    onNext={() =>
                        setCurrentPage((prev) =>
                            Math.min(totalPages - 1, prev + 1),
                        )
                    }
                />
            )}
        </Card>
    )
}
